import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Select, InputNumber, Button, Table, Space, Popconfirm, Tag, Input, message } from 'antd';
import { DeleteOutlined, QuestionCircleOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useFurnizori } from '../hooks/useFurnizori';

const { Option } = Select;

function LinkFurnizorModal({ visible, produs, onSubmit, onCancel, onRefresh }) {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editingRow, setEditingRow] = useState(null);
  const [produseFurnizori, setProdusFurnizori] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  const { 
    furnizori, 
    fetchFurnizori,
    createProdusToFurnizorLink,
    updateProdusToFurnizorLink,
    deleteProdusToFurnizorLink,
    getFurnizoriForProdus
  } = useFurnizori();

  // Încarcă furnizorii și legăturile pentru produs
  const loadData = useCallback(async () => {
    if (visible && produs?.id) {
      console.log('Loading data for product:', produs.id);
      fetchFurnizori(1, 100); // Încarcă toți furnizorii
      
      setLoadingLinks(true);
      try {
        const links = await getFurnizoriForProdus(produs.id);
        setProdusFurnizori(links);
      } catch (error) {
        console.error('Eroare la încărcarea legăturilor:', error);
      } finally {
        setLoadingLinks(false);
      }
    }
  }, [visible, produs?.id, fetchFurnizori, getFurnizoriForProdus]);

  useEffect(() => {
    loadData();
    if (visible) {
      form.resetFields();
    } else {
      form.resetFields();
      setEditingRow(null);
      editForm.resetFields();
    }
  }, [visible, loadData, form, editForm]);

  const handleSubmit = async (values) => {
    const tempId = Date.now().toString(); // ID temporar pentru optimizare
    
    try {
      const linkData = {
        mysql_id_produs: produs.id,
        mongo_id_furnizor: values._id,
        pret: values.pret,
        valuta: values.valuta,
        product_url: values.product_url || null,
        data_verificare: new Date().toISOString()
      };
      
      // Actualizare optimistă
      const newLink = { ...linkData, id: tempId };
      setProdusFurnizori(prev => [newLink, ...prev]);
      
      const result = await createProdusToFurnizorLink(linkData);
      if (result) {
        // Actualizăm lista cu datele reale de la server
        setProdusFurnizori(prev => 
          prev.map(link => link.id === tempId ? { ...result, id: result.id } : link)
        );
        form.resetFields();
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      // Revertim schimbarea în caz de eroare
      setProdusFurnizori(prev => prev.filter(link => link.id !== tempId));
      message.error('A apărut o eroare la adăugarea furnizorului');
      console.error('Eroare la adăugarea furnizorului:', error);
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      // Actualizare optimistă
      setProdusFurnizori(prev => prev.filter(link => link.id !== linkId));
      
      const result = await deleteProdusToFurnizorLink(linkId);
      if (!result) {
        // Revertim schimbarea în caz de eroare
        loadData();
        message.error('A apărut o eroare la ștergerea legăturii');
      } else {
        // Apelăm onRefresh pentru a actualiza lista de produse
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      loadData();
      message.error('A apărut o eroare la ștergerea legăturii');
      console.error('Eroare la ștergerea legăturii:', error);
    }
  };

  const handleEditPrice = (record) => {
    setEditingRow(record.id);
    editForm.setFieldsValue({
      pret: record.pret,
      valuta: record.valuta
    });
  };

  const handleSaveEdit = async (linkId) => {
    try {
      const values = await editForm.validateFields();
      
      // Actualizare optimistă
      setProdusFurnizori(prev => 
        prev.map(link => 
          link.id === linkId 
            ? { ...link, pret: values.pret, valuta: values.valuta }
            : link
        )
      );
      
      const result = await updateProdusToFurnizorLink(linkId, values);
      
      if (result) {
        setEditingRow(null);
        editForm.resetFields();
        if (onRefresh) onRefresh();
      } else {
        // Revertim schimbarea în caz de eroare
        loadData();
        message.error('A apărut o eroare la actualizarea prețului');
      }
    } catch (error) {
      loadData();
      message.error('A apărut o eroare la actualizarea prețului');
      console.error('Eroare la actualizarea prețului:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    editForm.resetFields();
  };

  const columns = [
    {
      title: 'Furnizor',
      dataIndex: 'mongo_id_furnizor',
      key: 'mongo_id_furnizor',
      render: (furnizorId) => {
        const furnizor = furnizori.find(f => f._id === furnizorId);
        return furnizor ? furnizor.denumire : furnizorId;
      },
    },
    {
      title: 'Preț',
      dataIndex: 'pret',
      key: 'pret',
      render: (pret, record) => {
        if (editingRow === record.id) {
          return (
            <div className="flex gap-2 items-center">
              <Form form={editForm} layout="inline" className="flex gap-1">
                <Form.Item
                  name="pret"
                  className="mb-0"
                  rules={[{ required: true, message: 'Prețul este obligatoriu' }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    size="small"
                    style={{ width: 80 }}
                  />
                </Form.Item>
                <Form.Item
                  name="valuta"
                  className="mb-0"
                  rules={[{ required: true, message: 'Valuta este obligatorie' }]}
                >
                  <Select size="small" style={{ width: 60 }}>
                    <Option value="MDL">MDL</Option>
                    <Option value="EUR">EUR</Option>
                    <Option value="USD">USD</Option>
                  </Select>
                </Form.Item>
              </Form>
            </div>
          );
        }
        return `${pret} ${record.valuta}`;
      },
    },
    {
      title: 'Data verificare',
      dataIndex: 'data_verificare',
      key: 'data_verificare',
      render: (data) => data ? new Date(data).toLocaleDateString('ro-RO') : '-',
    },
    {
      title: 'URL Produs',
      dataIndex: 'product_url',
      key: 'product_url',
      render: (product_url) => product_url ? <Button type="link" onClick={() => window.open(product_url, '_blank')}>Link Produs</Button> : '-',
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_, record) => (
        <div className="flex gap-2">
          {editingRow === record.id ? (
            <>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                size="small"
                onClick={() => handleSaveEdit(record.id)}
              />
              <Button 
                icon={<CloseOutlined />} 
                size="small"
                onClick={handleCancelEdit}
              />
            </>
          ) : (
            <>
              <Button 
                icon={<EditOutlined />} 
                size="small"
                onClick={() => handleEditPrice(record)}
                title="Editează prețul"
              />
              <Popconfirm
                title="Ești sigur că vrei să ștergi această legătură?"
                onConfirm={() => handleDeleteLink(record.id)}
                okText="Da"
                cancelText="Nu"
              >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>

              <Popconfirm
                title="Verificați prețul la furnizor?"
                onConfirm={() => {/* TODO: implement check price */}}
                okText="Da"
                cancelText="Nu"
              >
                <Button icon={<QuestionCircleOutlined />} size="small" />
              </Popconfirm>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Furnizori pentru: ${produs?.denumire}`}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium mb-4">Adaugă furnizor nou</h3>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item
                name="_id"
                label="Furnizor"
                rules={[{ required: true, message: 'Selectează furnizorul' }]}
              >
                <Select placeholder="Selectează furnizorul">
                  {furnizori.map(furnizor => (
                    <Option key={furnizor._id} value={furnizor._id}>
                      {furnizor.denumire}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="pret"
                label="Preț"
                rules={[{ required: true, message: 'Introduceți prețul' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                name="valuta"
                label="Valută"
                rules={[{ required: true, message: 'Selectează valuta' }]}
                initialValue="MDL"
              >
                <Select>
                  <Option value="MDL">MDL</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="USD">USD</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="product_url"
              label="URL Produs (opțional)"
            >
              <Input placeholder="https://..." />
            </Form.Item>

            <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700">
              Adaugă furnizor
            </Button>
          </Form>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-4">Furnizori existenți</h3>
          <Table
            columns={columns}
            dataSource={produseFurnizori}
            rowKey="id"
            pagination={false}
            size="small"
            loading={loadingLinks}
          />
        </div>
      </div>
    </Modal>
  );
}

export default LinkFurnizorModal; 